import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

// Partners used to co-brand impact reports (served by the communication
// service through the gateway).
export const PARTNERS_PATH = '/report-partners';
const KEY = ['report-partners'];

export const useReportPartners = (status = 'active') =>
  useQuery(
    [...KEY, status],
    async () =>
      (await axiosInstancev2.get(PARTNERS_PATH, { params: { status } })).data
        ?.partners ?? [],
    { refetchOnWindowFocus: false, retry: false, staleTime: 60 * 1000 }
  );

const toForm = (values) => {
  const form = new FormData();
  Object.entries(values).forEach(([key, value]) => {
    if (value === undefined) return;
    if (key === 'default_recipients') {
      form.append(key, JSON.stringify(value));
    } else if (value instanceof Blob) {
      form.append(key, value, value.name || 'logo');
    } else {
      form.append(key, String(value));
    }
  });
  return form;
};

export const useSavePartner = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, values }) =>
      id
        ? axiosInstancev2.patch(`${PARTNERS_PATH}/${id}`, toForm(values))
        : axiosInstancev2.post(PARTNERS_PATH, toForm(values)),
    { onSuccess: () => queryClient.invalidateQueries(KEY) }
  );
};

// The logo comes through the API (with auth) as a blob, so drawing it on a
// canvas for the PDF isn't blocked by cross-origin rules.
export const fetchPartnerLogo = async (partner) => {
  if (!partner?.has_logo) return null;
  const response = await axiosInstancev2.get(
    `${PARTNERS_PATH}/${partner.id}/logo`,
    { params: { v: partner.logo_version }, responseType: 'blob' }
  );
  return URL.createObjectURL(response.data);
};

export const partnerError = (error, fallback) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.error;
  if (status === 404 && !message) {
    return "Report partners aren't available on the server yet.";
  }
  if (status === 409)
    return message || 'A partner with that name already exists.';
  if (status === 413) return 'The logo is too large (1 MB at most).';
  if (status === 415) return 'Use a PNG or JPEG logo.';
  if (status === 403) {
    return (
      message || 'You need the SendImpactReport permission to manage partners.'
    );
  }
  return message || fallback;
};
